import React, { useMemo } from "react";
import { Text as RNText, TextProps, StyleProp, TextStyle, Platform } from "react-native";
import { getVariants } from "@utils/styles";
import { scaleStyle } from "@utils/scale";

interface CustomTextProps extends TextProps {
  variant?: string;
  newfont?: boolean;
  children?: React.ReactNode;
}

const CustomText: React.FC<CustomTextProps> = (props) => {
  const { variant, children, style, newfont = false, ...rest } = props;
  
  const scaledVariantStyle = useMemo((): StyleProp<TextStyle> | undefined => {
    const variantKey = variant ?? "blank";
    const variantStyle = getVariants(variantKey, newfont);
    
    if (!variantStyle) {
      return undefined;
    }
    
    // Scale the variant style - this will scale fontSize, lineHeight, margins, etc.
    try {
      const scaled = scaleStyle(variantStyle as TextStyle);
      // Ensure we have a valid style object
      if (scaled && Object.keys(scaled).length > 0) {
        return scaled as TextStyle;
      }
    } catch (error) {
      // If scaling fails, fall back to unscaled variant
      console.warn('Failed to scale variant style:', error);
    }
    
    // Fallback to unscaled variant
    return variantStyle as TextStyle;
  }, [variant, newfont]);
  
  // Build style array, filtering out undefined values
  // Add vertical alignment fix for lineHeight
  const finalStyle = useMemo(() => {
    const styles = [];
    if (scaledVariantStyle) {
      styles.push(scaledVariantStyle);
    }
    if (style) {
      styles.push(style);
    }
    
    // Add Android-specific style fix for vertical centering with lineHeight
    if (Platform.OS === 'android' && scaledVariantStyle) {
      const variantStyleObj = scaledVariantStyle as TextStyle;
      if (variantStyleObj.lineHeight !== undefined) {
        // Ensure text is vertically centered when lineHeight is set
        styles.push({
          textAlignVertical: 'center' as const,
        });
      }
    }
    
    return styles.length > 0 ? styles : undefined;
  }, [scaledVariantStyle, style]);
  
  // Android: Always disable font padding to ensure proper vertical alignment
  // This is especially important when lineHeight is used
  // includeFontPadding: false removes extra padding that causes misalignment
  const androidProps = useMemo(() => {
    if (Platform.OS === 'android') {
      return {
        includeFontPadding: false,
      };
    }
    return {};
  }, []);
  
  return (
    <RNText
      style={finalStyle}
      {...androidProps}
      {...rest}
    >
      {children}
    </RNText>
  );
};

export default CustomText;

